import {MapIcon, MapPin, Phone} from "lucide-react";
import clsx from "clsx";
import {type Practice} from "./PracticesMapContainer.tsx";
import type {GeoHit} from "instantsearch.js";
import {trackContactClick, trackPracticeClick, trackViewProviderClick} from "../lib/algolia-insights";

interface PracticeCardProps {
    practice: GeoHit<Practice>;
    isSelected: boolean;
    isMobileDevice?: boolean;
    userPickedLocation?: google.maps.places.PlaceResult;
    onClick?: (practice: Practice) => void;
}

const PracticeCard = ({practice, isSelected, isMobileDevice, userPickedLocation, onClick}: PracticeCardProps) => {
    return (
        <div
            className={
                clsx(
                    `card rounded-md block transition-transform shadow-md `,
                    {'scale-105 shadow-2xl': isSelected},
                    {'group cursor-pointer': !isMobileDevice},
                    {'bg-gray-50': isMobileDevice}
                )}
            onClick={(ev) => {
                if (isMobileDevice || !onClick) {
                    return;
                }

                if ((ev.target as HTMLElement)?.tagName === 'A') {
                    return
                }

                trackPracticeClick(practice);
                onClick(practice)
                ev.preventDefault()
            }}>
            <div
                className={clsx("header rounded-t-lg p-4 bg-blue-900 text-white")}>
                <div
                    className={clsx("flex py-0 relative items-end gap-2", {'flex-wrap': isMobileDevice})}>
                    <div className={clsx(isMobileDevice ? "basis-full" : "flex-grow")}>
                        <p className={"text-xs opacity-70 tracking-wide flex items-start gap-1 text-green-600 font-medium"}>
                            <MapPin size={14}/>
                            <span>{practice.address.split(' ').slice(Math.max(practice.address.split(' ').length - 3, 1)).join(' ')}</span>
                        </p>
                        <h2 className={"font-semibold"}>
                            {practice.name}
                        </h2>
                    </div>
                    <div className={clsx(isMobileDevice ? "basis-full" : "flex-none")}>
                        {userPickedLocation &&
                            <p className={"text-xs opacity-70 font-light absolute -top-2 right-2"}>
                                {practice._rankingInfo?.geoDistance.toFixed(2)} mi. from you
                            </p>}
                        <a href={`/hormone-replacement-therapy-provider/${practice.slug}`}
                           className={"p-1 px-3 provider-page-button"}
                           onClick={() => trackViewProviderClick(practice)}>
                            View Provider
                        </a>
                    </div>
                </div>
            </div>
            <div
                className={`body rounded-b-lg p-4 shadow group-hover:bg-blue-100 ${isSelected && 'bg-blue-100'}`}>
                <div className="flex justify-between">
                    <div className={clsx(isMobileDevice ? "flex-grow" : "flex-none")}>
                        <div className={"address max-w-[190px]"}>
                                <span className={"block mb-2 text-sm opacity-85 max-w-40"}>
                                {practice.address}
                                </span>
                            <a target={'_blank'}
                               href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(practice.address)}`}
                               className={"bg-green-700 hover:bg-green-900 text-sm rounded-md text-white p-1 px-4 py-1 text-center w-fit mt-1 flex gap-2 items-center"}
                               onClick={() => trackContactClick(practice, 'directions')}>
                                <MapIcon className={"pointer-events-none"} size={18}/> <span
                                className={"pointer-events-none"}>Get Directions</span>
                            </a>
                        </div>
                    </div>
                    <div className={"flex-none"}>
                        <div>
                            <a href={`tel:${practice.phone}`}
                               className={"flex items-center gap-1 text-sm font-bold hover:text-green-600"}
                               onClick={() => trackContactClick(practice, 'phone')}>
                                        <span
                                            className={"pointer-events-none inline-block p-1 text-white rounded-full bg-green-600"}><Phone
                                            fill={'#fff'} strokeWidth={0} size={12}/></span>
                                <span
                                    className={"pointer-events-none"}>{practice.phone_formatted}</span>
                            </a>
                        </div>
                        <div className={"mt-4"}>
                            <div className={"text-[10px] uppercase opacity-70"}>Serving</div>
                            <div className={"flex gap-2"}>
                                        <span
                                            className={"uppercase text-[10px] font-black tracking-widest text-green-700 inline-block px-1 py-0.5 bg-green-200"}>Women</span>
                                <span
                                    className={"uppercase text-[10px] font-black tracking-widest text-blue-700 inline-block px-1 py-0.5 bg-blue-200"}>Men</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PracticeCard;
